import React, { useEffect, useState, useCallback } from 'react';
import { researchosService } from '../../services/researchosService';
import { useSortableRows, SortableTh } from '../../components/tables/SortableTable';

const GOLD = 'rgb(var(--brand-gold-rgb))';

function fmtDate(s) {
  if (!s) return '';
  try { return new Date(s).toISOString().slice(0, 10); } catch { return ''; }
}

// §D relationship map — the DETERMINISTIC co-occurrence graph. Two nodes are linked when they show
// up in the same videos MORE than their individual popularity predicts (nPMI ∈ [−1,1], base-rate
// normalized so a hub like "AI" doesn't dominate). A bounded LLM pass optionally labels the top
// edges with what the relationship IS, grounded in the co-occurring video titles (the receipts).

const KIND_STYLE = {
  supply_chain: '#60a5fa', competition: '#f87171', thematic_driver: GOLD,
  policy_regulatory: '#c084fc', capital_flow: '#34d399', customer_partner: '#38bdf8',
  coincidence: '#9ca3af',
};

function StatTile({ label, value }) {
  return (
    <div className="rounded-xl border border-gray-200 px-4 py-3 dark:border-gray-800">
      <div className="font-serif text-2xl tabular-nums text-gray-900 dark:text-gray-100">{value}</div>
      <div className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400 dark:text-gray-500">{label}</div>
    </div>
  );
}

function nodeLabel(side, fallback) {
  if (!side || typeof side !== 'object') return fallback;
  return side.label || side.ticker || side.name || fallback;
}

const COLS = [
  { key: 'a', type: 'string', get: (r) => nodeLabel(r.a_label, r.a) },
  { key: 'b', type: 'string', get: (r) => nodeLabel(r.b_label, r.b) },
  { key: 'cooccur', type: 'number', get: (r) => r.cooccur },
  { key: 'lift', type: 'number', get: (r) => r.lift },
  { key: 'npmi', type: 'number', get: (r) => r.npmi },
];

function EdgeTable({ edges, maxNpmi }) {
  const { sorted, toggle, indicator } = useSortableRows(edges, COLS, { key: 'npmi', dir: 'desc' });
  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full min-w-[640px] text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-[10px] uppercase tracking-[0.12em] text-gray-400 dark:border-gray-800 dark:text-gray-500">
            <SortableTh label="Node A" colKey="a" toggle={toggle} indicator={indicator} align="left" className="py-2" />
            <SortableTh label="Node B" colKey="b" toggle={toggle} indicator={indicator} align="left" />
            <SortableTh label="Co" colKey="cooccur" toggle={toggle} indicator={indicator} />
            <SortableTh label="Lift" colKey="lift" toggle={toggle} indicator={indicator} />
            <th className="px-3 text-right">Strength (nPMI)</th>
            <th className="text-left">What it is</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((e) => {
            const pct = maxNpmi ? Math.max(3, Math.round((e.npmi / maxNpmi) * 100)) : 0;
            const llm = e.llm;
            return (
              <tr key={`${e.a}|${e.b}`} className="border-b border-gray-100 last:border-0 dark:border-gray-800/60">
                <td className="py-2 pr-2 text-gray-800 dark:text-gray-100">{nodeLabel(e.a_label, e.a)}</td>
                <td className="pr-2 text-gray-800 dark:text-gray-100">{nodeLabel(e.b_label, e.b)}</td>
                <td className="text-right tabular-nums text-gray-500">{e.cooccur}</td>
                <td className="text-right tabular-nums text-gray-500">{e.lift?.toFixed(2)}</td>
                <td className="px-3">
                  <div className="flex items-center justify-end gap-2">
                    <span className="relative hidden h-3 w-20 overflow-hidden rounded bg-gray-100 dark:bg-gray-800/60 sm:block">
                      <span className="absolute inset-y-0 left-0 rounded" style={{ width: `${pct}%`, background: GOLD, opacity: 0.85 }} />
                    </span>
                    <span className="w-12 text-right tabular-nums font-medium text-gray-700 dark:text-gray-200">{e.npmi?.toFixed(3)}</span>
                  </div>
                </td>
                <td className="py-1.5 text-left">
                  {llm ? (
                    <div className="flex items-center gap-2">
                      <span className="shrink-0 rounded px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide"
                        style={{ color: KIND_STYLE[llm.kind] || '#9ca3af', border: `1px solid ${KIND_STYLE[llm.kind] || '#9ca3af'}55` }}>
                        {(llm.kind || '—').replace(/_/g, ' ')}
                      </span>
                      <span className={`text-[12px] ${llm.real ? 'text-gray-600 dark:text-gray-300' : 'italic text-gray-400 line-through'}`}>{llm.label}</span>
                    </div>
                  ) : (
                    <span className="text-[11px] text-gray-300 dark:text-gray-600">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ── Source-document modal (lazy) — opens a receipt so a reviewer can verify the edge ────── */
function DocModal({ id, onClose }) {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    researchosService.getIngestDocument(id)
      .then((r) => { if (alive) setDoc(r.data); })
      .catch(() => { if (alive) setErr('Failed to load the source document.'); });
    return () => { alive = false; };
  }, [id]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-2 sm:p-6" onClick={onClose}>
      <div className="relative w-full max-w-3xl rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 flex items-start justify-between gap-3 border-b border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
          <div className="min-w-0">
            <h3 className="truncate font-serif text-base text-gray-900 dark:text-gray-100">{doc?.title || (err ? 'Error' : 'Loading…')}</h3>
            {doc && (
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {doc.source_ref} · {fmtDate(doc.published_at)} · {doc.source_type}
                {doc.url && <> · <a href={doc.url} target="_blank" rel="noreferrer" style={{ color: GOLD }}>source ↗</a></>}
              </p>
            )}
          </div>
          <button className="shrink-0 rounded px-2 py-1 text-sm text-gray-500 hover:text-gray-800 dark:hover:text-gray-200" onClick={onClose}>✕</button>
        </div>
        <div className="max-h-[75vh] overflow-y-auto p-4 scrollbar-thin">
          {err && <p className="text-sm text-red-600 dark:text-red-400">{err}</p>}
          {!doc && !err && <p className="text-sm text-gray-500 dark:text-gray-400">Loading document…</p>}
          {doc && <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700 dark:text-gray-300">{doc.raw_text}</p>}
        </div>
      </div>
    </div>
  );
}

const REL_COLS = [
  { key: 'subject', type: 'string', get: (r) => r.subject_ticker || r.subject },
  { key: 'relation', type: 'string', get: (r) => r.relation },
  { key: 'object', type: 'string', get: (r) => r.object_ticker || r.object },
  { key: 'count', type: 'number', get: (r) => r.count },
  { key: 'scope', type: 'number', get: (r) => (r.market_wide_prob == null ? -1 : r.market_wide_prob) },
];

const PAGE_SIZE = 50;

// Relations Review — browse ALL typed GraphRelation edges to spot noise/near-dupes against source.
function RelationsReview() {
  const [relation, setRelation] = useState(null);   // null = all types
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [openKey, setOpenKey] = useState(null);      // expanded row
  const [openDoc, setOpenDoc] = useState(null);      // receipt modal doc id

  // debounce the search box (300ms) and reset to page 1 on a new query
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q.trim()); setOffset(0); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr('');
    researchosService.getRelations({ relation: relation || undefined, q: debouncedQ || undefined,
                                     offset, limit: PAGE_SIZE })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setErr('Could not load relations.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [relation, debouncedQ, offset]);

  const rows = (data && data.rows) || [];
  const total = (data && data.total) || 0;
  const relationCounts = (data && data.relation_counts) || {};
  const relTypes = Object.entries(relationCounts).sort((a, b) => b[1] - a[1]);
  const { sorted, toggle, indicator } = useSortableRows(rows, REL_COLS, { key: 'count', dir: 'desc' });

  const from = total ? offset + 1 : 0;
  const to = Math.min(offset + rows.length, total);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="font-serif text-xl text-gray-900 dark:text-gray-100">Relations review</h2>
        <p className="mt-0.5 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
          Every STATED relationship the extractor pulled, grouped by (subject, relation, object).
          Filter by type, search a name, expand a row to read the reasoning and open the receipts —
          this is where off-topic noise (brother_of, coach_of) is easy to spot and verify.
        </p>
      </div>

      {/* relation-type pills — count is number of distinct groups of that type */}
      <div className="flex flex-wrap gap-1.5">
        <button onClick={() => { setRelation(null); setOffset(0); }}
          className="rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors"
          style={relation === null ? { background: GOLD, color: '#1a1a1a', borderColor: GOLD }
                                    : { borderColor: '#d1d5db55', color: '#6b7280' }}>
          All <span className="tabular-nums opacity-70">{Object.values(relationCounts).reduce((a, b) => a + b, 0)}</span>
        </button>
        {relTypes.map(([rel, n]) => (
          <button key={rel} onClick={() => { setRelation(rel); setOffset(0); }}
            className="rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors"
            style={relation === rel ? { background: GOLD, color: '#1a1a1a', borderColor: GOLD }
                                     : { borderColor: '#d1d5db55', color: '#6b7280' }}>
            {rel.replace(/_/g, ' ')} <span className="tabular-nums opacity-70">{n}</span>
          </button>
        ))}
      </div>

      {/* search + count header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search subject / object / ticker…"
          className="w-full max-w-xs rounded-md border border-gray-300 bg-transparent px-3 py-1.5 text-sm text-gray-800 placeholder-gray-400 focus:border-gray-400 focus:outline-none dark:border-gray-700 dark:text-gray-100"
        />
        <div className="text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400">
          {total.toLocaleString()} groups{relation ? ` · ${relation.replace(/_/g, ' ')}` : ''}
        </div>
      </div>

      {err && <div className="text-sm text-red-500">{err}</div>}

      <div className="rounded-2xl border border-gray-200 p-2 dark:border-gray-800 sm:p-4">
        {loading ? (
          <div className="py-6 text-center text-sm text-gray-400">Loading relations…</div>
        ) : !rows.length ? (
          <div className="py-6 text-center text-sm text-gray-400">No relations match this filter.</div>
        ) : (
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full min-w-[680px] text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-[10px] uppercase tracking-[0.12em] text-gray-400 dark:border-gray-800 dark:text-gray-500">
                  <SortableTh label="Subject" colKey="subject" toggle={toggle} indicator={indicator} align="left" className="py-2" />
                  <SortableTh label="Relation" colKey="relation" toggle={toggle} indicator={indicator} align="left" />
                  <SortableTh label="Object" colKey="object" toggle={toggle} indicator={indicator} align="left" />
                  <SortableTh label="# Docs" colKey="count" toggle={toggle} indicator={indicator} />
                  <SortableTh label="Scope" colKey="scope" toggle={toggle} indicator={indicator} />
                  <th className="px-3 text-right">Sources</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => {
                  const key = `${r.subject}|${r.relation}|${r.object}`;
                  const expanded = openKey === key;
                  const showScope = r.relation === 'competitor_of' && r.market_wide_prob != null;
                  const scopeColor = !showScope ? undefined
                    : r.market_wide_prob > 0.5 ? '#16a34a' : r.market_wide_prob < 0.5 ? '#dc2626' : '#9ca3af';
                  return (
                    <React.Fragment key={key}>
                      <tr onClick={() => setOpenKey(expanded ? null : key)}
                        className="cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-gray-800/60 dark:hover:bg-gray-800/30">
                        <td className="py-2 pr-2 text-left text-gray-800 dark:text-gray-100">
                          <span className="text-gray-400">{expanded ? '▾ ' : '▸ '}</span>{r.subject}
                        </td>
                        <td className="pr-2 text-left">
                          <span className="rounded px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-gray-400" style={{ border: '1px solid #d1d5db55' }}>
                            {r.relation.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="pr-2 text-left text-gray-800 dark:text-gray-100">{r.object}</td>
                        <td className="text-right tabular-nums text-gray-500">{r.count}</td>
                        <td className="text-right tabular-nums font-medium" style={scopeColor ? { color: scopeColor } : undefined}>
                          {showScope ? `${Math.round(r.market_wide_prob * 100)}%` : ''}
                        </td>
                        <td className="px-3 text-right tabular-nums text-gray-400">{(r.receipts || []).length}</td>
                      </tr>
                      {expanded && (
                        <tr className="border-b border-gray-100 bg-gray-50/60 dark:border-gray-800/60 dark:bg-gray-800/20">
                          <td colSpan={6} className="px-3 py-3">
                            {r.note ? (
                              <p className="mb-2 max-w-3xl text-[13px] italic leading-relaxed text-gray-600 dark:text-gray-300">“{r.note}”</p>
                            ) : (
                              <p className="mb-2 text-[12px] text-gray-400">No reasoning captured for this edge.</p>
                            )}
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-[10px] uppercase tracking-wider text-gray-400">Receipts:</span>
                              {(r.receipts || []).length ? r.receipts.map((rc) => (
                                <button key={rc.doc_id} onClick={(e) => { e.stopPropagation(); setOpenDoc(rc.doc_id); }}
                                  className="max-w-[16rem] truncate rounded px-2 py-0.5 text-[11px] hover:bg-white dark:hover:bg-gray-800"
                                  style={{ border: `1px solid ${GOLD}55`, color: GOLD }}
                                  title={rc.title}>
                                  {rc.title}
                                </button>
                              )) : <span className="text-[11px] text-gray-400">none</span>}
                              {r.published_at && <span className="text-[10px] text-gray-400">latest {fmtDate(r.published_at)}</span>}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* pagination */}
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span className="tabular-nums">{from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}</span>
        <div className="flex items-center gap-2">
          <button onClick={() => setOffset(Math.max(offset - PAGE_SIZE, 0))} disabled={offset <= 0 || loading}
            className="rounded-md border px-3 py-1 font-medium transition-colors disabled:opacity-40" style={{ borderColor: '#d1d5db55' }}>
            ← Prev
          </button>
          <button onClick={() => setOffset(offset + PAGE_SIZE)} disabled={to >= total || loading}
            className="rounded-md border px-3 py-1 font-medium transition-colors disabled:opacity-40" style={{ borderColor: '#d1d5db55' }}>
            Next →
          </button>
        </div>
      </div>

      {openDoc != null && <DocModal id={openDoc} onClose={() => setOpenDoc(null)} />}
    </div>
  );
}

function RelationshipAffinityMap({ isAdmin = false }) {
  const [level, setLevel] = useState('bucket');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [labeling, setLabeling] = useState(false);

  const load = useCallback((lvl) => {
    setLoading(true); setErr('');
    // entity graph is sparser — require only 2 shared docs; buckets default the same but are denser
    researchosService.getRelationshipGraph({ level: lvl, min_cooccur: 2 })
      .then((r) => setData(r.data))
      .catch(() => setErr('Could not load the relationship graph.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(level); }, [level, load]);

  const generateLabels = () => {
    setLabeling(true);
    researchosService.labelRelationshipGraph({ level, label_top_n: 12 })
      .then(() => load(level))
      .catch(() => setErr('Label generation failed (is the cheap-model key set?).'))
      .finally(() => setLabeling(false));
  };

  const s = data || {};
  const edges = s.edges || [];
  const maxNpmi = edges.length ? Math.max(...edges.map((e) => e.npmi || 0)) : 0;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl text-gray-900 dark:text-gray-100">Relationship map</h2>
          <p className="mt-0.5 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
            What gets discussed together — above chance. Edges are base-rate-normalized (nPMI), so a
            popular node doesn’t link to everything; only genuine affinities surface.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5 dark:border-gray-800">
            {['bucket', 'entity'].map((lvl) => (
              <button key={lvl} onClick={() => setLevel(lvl)}
                className="rounded-md px-3 py-1 text-xs font-medium capitalize transition-colors"
                style={level === lvl ? { background: GOLD, color: '#1a1a1a' } : { color: '#6b7280' }}>
                {lvl === 'bucket' ? 'Themes' : 'Companies'}
              </button>
            ))}
          </div>
          {isAdmin && (
            <button onClick={generateLabels} disabled={labeling || !edges.length}
              className="rounded-md border px-3 py-1 text-xs font-medium transition-colors disabled:opacity-40"
              style={{ borderColor: `${GOLD}66`, color: GOLD }}>
              {labeling ? 'Labeling…' : s.labels_generated ? 'Re-label edges' : 'Label edges (AI)'}
            </button>
          )}
        </div>
      </div>

      {err && <div className="text-sm text-red-500">{err}</div>}
      {loading ? (
        <div className="text-sm text-gray-400">Loading graph…</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <StatTile label="Documents" value={s.n_docs ?? '—'} />
            <StatTile label="Nodes" value={(s.nodes || []).length} />
            <StatTile label="Edges" value={edges.length} />
          </div>

          <div className="rounded-2xl border border-gray-200 p-4 dark:border-gray-800">
            <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.15em]" style={{ color: GOLD }}>
              Above-chance affinities · by strength
            </div>
            {edges.length ? <EdgeTable edges={edges} maxNpmi={maxNpmi} />
              : <div className="py-6 text-center text-sm text-gray-400">No edges clear the co-occurrence floor yet.</div>}
          </div>

          {(s.explicit_relations || []).length > 0 && (
            <div className="rounded-2xl border border-gray-200 p-4 dark:border-gray-800">
              <div className="mb-3 text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400 dark:text-gray-500">
                Stated relationships · {s.explicit_relations.length} distinct (from the transcripts)
              </div>
              <div className="flex flex-col gap-1.5">
                {s.explicit_relations.slice(0, 24).map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-[13px]">
                    <span className="font-medium text-gray-800 dark:text-gray-100">{r.subject}</span>
                    <span className="rounded px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-gray-400" style={{ border: '1px solid #d1d5db55' }}>
                      {r.relation.replace(/_/g, ' ')}
                    </span>
                    <span className="text-gray-800 dark:text-gray-100">{r.object}</span>
                    {r.count > 1 && <span className="text-[11px] tabular-nums text-gray-400">×{r.count}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Relationships tab = the deterministic co-occurrence "Affinity map" + a "Relations review" table
// for browsing/verifying every typed GraphRelation edge. One section toggle switches between them.
export default function RelationshipMap({ isAdmin = false }) {
  const [section, setSection] = useState('map');
  return (
    <div className="flex flex-col gap-5">
      <div className="inline-flex self-start rounded-lg border border-gray-200 p-0.5 dark:border-gray-800">
        {[['map', 'Affinity map'], ['relations', 'Relations review']].map(([id, label]) => (
          <button key={id} onClick={() => setSection(id)}
            className="rounded-md px-3 py-1 text-xs font-medium transition-colors"
            style={section === id ? { background: GOLD, color: '#1a1a1a' } : { color: '#6b7280' }}>
            {label}
          </button>
        ))}
      </div>
      {section === 'map' ? <RelationshipAffinityMap isAdmin={isAdmin} /> : <RelationsReview />}
    </div>
  );
}
